import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import MinHeapEngine from "../AdvanceTreesEngine/MinHeapEngine";
import MaxHeapEngine from "../AdvanceTreesEngine/MaxHeapEngine";
import TrieEngine from "../AdvanceTreesEngine/TrieEngine";
import styles from "../Advancetrees.module.css";

type Traversal = "preorder" | "inorder" | "postorder" | "levelorder";

function buildMaxHeap(arr: number[]) {
  const a = [...arr];
  const n = a.length;

  const heapifyDown = (i: number) => {
    let largest = i;
    const left = 2 * i + 1;
    const right = 2 * i + 2;
    if (left < n && a[left] > a[largest]) largest = left;
    if (right < n && a[right] > a[largest]) largest = right;
    if (largest !== i) {
        [a[i], a[largest]] = [a[largest], a[i]];
        heapifyDown(largest);
    }
  };

    for (let i = Math.floor(n / 2) - 1; i >= 0; i--) heapifyDown(i);
  return a;
}

function buildMinHeap(arr: number[]) {
  const a = [...arr];
  const n = a.length;

  const heapifyDown = (i: number) => {
    let smallest = i;
    const left = 2 * i + 1;
    const right = 2 * i + 2;

    if (left < n && a[left] < a[smallest]) smallest = left;
    if (right < n && a[right] < a[smallest]) smallest = right;

    if (smallest !== i) {
      [a[i], a[smallest]] = [a[smallest], a[i]];
      heapifyDown(smallest);
    }
  };

  for (let i = Math.floor(n / 2) - 1; i >= 0; i--) heapifyDown(i);
  return a;
}
const CompareAdvancedTrees: React.FC = () => {
  const navigate = useNavigate();
  const [nodeValues, setNodeValues] = useState<number[]>([]);
  const [wordValues, setWordValues] = useState<string[]>([]);
  const [traversal, setTraversal] = useState<Traversal | null>(null);
  const [highlightLine, setHighlightLine] = useState<number | null>(null);
  const [highlightedNodesMin, setHighlightedNodesMin] = useState<number[]>([]);
  const [highlightedNodesMax, setHighlightedNodesMax] = useState<number[]>([]);
  const [highlightedWordsTrie, setHighlightedWordsTrie] = useState<string[]>([]);
  const [resetTrigger, setResetTrigger] = useState<boolean>(false);
  const [generateTrigger, setGenerateTrigger] = useState<number>(0);
  const [minHeapArray, setMinHeapArray] = useState<number[]>([]);
  const [maxHeapArray, setMaxHeapArray] = useState<number[]>([]);
  const noopActiveLine = () => {};
  const minHeapBuilt = buildMinHeap(nodeValues);
  const maxHeapBuilt = buildMaxHeap(nodeValues);  

  const handleGenerate = () => {
    const numbers = Array.from({ length: 6 }, () => Math.floor(Math.random() * 90) + 10);
    const wordsSets = [
      ["cat", "car", "cart", "dog", "dot"],
      ["sun", "sand", "song", "sip", "sit"],
      ["app", "apple", "apply", "apt", "ape"],
    ];
    const randomWords = wordsSets[Math.floor(Math.random() * wordsSets.length)];

    setNodeValues(numbers);
    setWordValues(randomWords);
    setTraversal(null);
    setHighlightLine(null);
    setHighlightedNodesMin([]);
    setHighlightedNodesMax([]);
    setHighlightedWordsTrie([]);
    setGenerateTrigger((prev) => prev + 1);
  };

  const handleReset = () => {
    setTraversal(null);
    setHighlightLine(null);
    setHighlightedNodesMin([]);
    setHighlightedNodesMax([]);
    setHighlightedWordsTrie([]);
    setResetTrigger((prev) => !prev);
  };

  return (
    <div className={styles.compareadancetreeContainer}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>
        ← Return
      </button>

      <h1 className={styles.title}>
        Compare Advanced Trees
      </h1>

      <p className={styles.description}>
        Imagine two systems built for speed, but optimized for completely different missions.<br />
        A <strong>Heap grows</strong> like a perfectly balanced pyramid — always keeping the smallest or largest item at the top — ideal for grabbing priorities fast. <strong>A Trie,</strong> 
        meanwhile, expands like a branching dictionary — each letter a step deeper — built for searching words in the time it takes to spell them. 
        They can store the same data, but their internal logic shapes what they're good at: <i>heaps excel at quick retrieval of “what’s next,” while tries dominate prefix matching and word lookup.</i>
        Same ingredients, different architecture — and dramatically different strengths.
      </p>

      <div className={styles.useCase}>
        <ul>
            <strong>Min Heap:</strong> Keeps the smallest value at the root.
            <strong>Max Heap:</strong> Keeps the largest value at the root.
            <strong>Trie:</strong> Organizes words by shared prefixes for fast lookups.
            All three use hierarchical structures, but each optimizes for
            different access patterns.
        </ul>

        <p>
            <i>This comparison uses a single set of randomly generated values to show how different advanced trees interpret the same input. 
            Min Heaps place the smallest value at the root, Max Heaps place the largest value at the root, and Tries organize words by shared prefixes. 
            Even when starting with the same data, their structures differ dramatically based on the rules each tree follows.</i>
        </p>
      </div>

      {/* Controls */}
      <div className={styles.controls}>
        <button onClick={handleGenerate}>Generate Data</button>
        <button onClick={handleReset}>Reset</button>
      </div>

      {/* Compare Grid */}
      <div className={styles.treesCompareGrid}>
        <div className={styles.treesCompareColumn}>
          <h2 className={styles.treesCompareHeader}>Min Heap</h2>
          <MinHeapEngine
            heapData={nodeValues}
            traversal={traversal}
            setHighlightLine={setHighlightLine}
            highlightedNodes={highlightedNodesMin}
            setHighlightedNodes={setHighlightedNodesMin}
            resetTrigger={resetTrigger}
            generateTrigger={generateTrigger}
            setActiveLine={noopActiveLine}
            setTraversalResult={setMinHeapArray}  
          />
          <div className={styles.resultBox}>
            <h3>Min Heap Data:</h3>
            <p>
              {nodeValues.length > 0
                ? minHeapBuilt.join(" → ")
                : "No Heap Built yet."}
            </p>
          </div>
        </div>

        {/* Max Heap */}
        <div className={styles.treesCompareColumn}>
          <h2 className={styles.treesCompareHeader}>Max Heap</h2>
          <MaxHeapEngine
            heapData={nodeValues}
            traversal={traversal}
            setHighlightLine={setHighlightLine}
            highlightedNodes={highlightedNodesMax}
            setHighlightedNodes={setHighlightedNodesMax}
            resetTrigger={resetTrigger}
            generateTrigger={generateTrigger}
            setActiveLine={noopActiveLine}
            setTraversalResult={setMaxHeapArray} 
          />
          <div className={styles.resultBox}>
            <h3>Max Heap Data:</h3>
            <p>
              {nodeValues.length > 0
                ? maxHeapBuilt.join(" → ")
                : "No Heap Built yet."}
            </p>
          </div>
        </div>

        {/* Trie */}
        <div className={styles.treesCompareColumn}>
          <h2 className={styles.treesCompareHeader}>Trie</h2>
          <TrieEngine
            trieWords={wordValues}
            operation={"searchPrefix"}
            inputWord={""}
            resetTrigger={resetTrigger}
            generateTrigger={generateTrigger}
            setActiveLine={() => {}}
            setHighlightedNodes={setHighlightedWordsTrie}
          />
          <div className={styles.resultBox}>
            <h3>Trie Words:</h3>
            <p>
              {wordValues.length === 0
                ? "No words generated yet."
                : wordValues.join(" → ")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompareAdvancedTrees;
